import React, { useEffect, useState } from "react";
import axios from "axios";
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Chip, 
  Button, 
  Dialog, 
  IconButton, 
  TextField, 
  Switch, 
  FormControlLabel,
  useTheme,
  Grid,
  Card,
  CardContent,
  Divider,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaymentIcon from "@mui/icons-material/Payment";
import DoneIcon from "@mui/icons-material/Done";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GroupIcon from "@mui/icons-material/Group";
import BusinessIcon from "@mui/icons-material/Business";
import ipconfig from "../../Core/ipconfig";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function SubscriptionDetailes({ data, readOnly }: any) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [licenses, setLicenses] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLicenseData, setModalLicenseData] = useState<any>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Form states for the modal to avoid window variable hacks
  const [serviceUpdates, setServiceUpdates] = useState<any>({
    accosync: { expiry: "", status: "", allowedUsers: "", allowedCompanies: "" },
    analytics: { expiry: "", status: "", allowedUsers: "", allowedCompanies: "" },
    mart: { expiry: "", status: "", allowedUsers: "", allowedCompanies: "" },
  });

  const fetchSubscriptionData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${ipconfig}/fetchuserbyid?userId=${data?._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
        setLicenses(response.data.connectorDetails || []);
      }
    } catch (error) {
      console.error("Error fetching subscription details:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkLicenseStatus = async (licenseNumber: string) => {
    setModalLoading(true);
    setIsModalOpen(true);
    // Reset local states
    setServiceUpdates({
      accosync: { expiry: "", status: "", allowedUsers: "", allowedCompanies: "" },
      analytics: { expiry: "", status: "", allowedUsers: "", allowedCompanies: "" },
      mart: { expiry: "", status: "", allowedUsers: "", allowedCompanies: "" },
    });

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ipconfig}/admin/subscriptions/status?licenseNumber=${licenseNumber}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status) {
        const licData = response.data.data;
        setModalLicenseData(licData);
        
        // Initialize state fields
        const initialUpdates: any = {};
        ["accosync", "analytics", "mart"].forEach(svc => {
          let service = licData[`${svc}optedservice`] || (licData.services && licData.services[svc]);
          if (svc === 'accosync' && !service && licData.services) {
            if (licData.services.status !== undefined) service = licData.services;
          }
          
          if (service) {
            initialUpdates[svc] = {
              status: service.status || "expired",
              allowedUsers: service.allowedUserAndDevices !== undefined ? service.allowedUserAndDevices.toString() : "",
              allowedCompanies: service.NumberOfAllowedCompanies !== undefined ? service.NumberOfAllowedCompanies.toString() : "",
              expiry: "",
            };
          } else {
            initialUpdates[svc] = { expiry: "", status: "", allowedUsers: "", allowedCompanies: "" };
          }
        });
        setServiceUpdates(initialUpdates);
      }
    } catch (error: any) {
      console.error("Error fetching license status:", error);
      toast.error(error?.response?.data?.message || "Error fetching license status");
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdateAll = async () => {
    setModalLoading(true);
    try {
      const licenseNumber = modalLicenseData.tallyLicenseNumber;
      const updates = [];
      
      for (const svc of ["accosync", "analytics", "mart"]) {
        const stateVal = serviceUpdates[svc];
        const newExpiryRaw = stateVal.expiry;
        const manualStatus = stateVal.status;
        const allowedUsers = stateVal.allowedUsers ? parseInt(stateVal.allowedUsers, 10) : undefined;
        const allowedCompanies = stateVal.allowedCompanies ? parseInt(stateVal.allowedCompanies, 10) : undefined;
        
        const formattedDate = newExpiryRaw ? newExpiryRaw.split('-').reverse().join('-') : undefined;
        
        // If anything was customized/provided, add to updates list
        if (formattedDate || manualStatus || allowedUsers !== undefined || allowedCompanies !== undefined) {
          updates.push({
            serviceType: svc,
            newExpiryDate: formattedDate,
            status: manualStatus,
            allowedUserAndDevices: allowedUsers,
            NumberOfAllowedCompanies: allowedCompanies
          });
        }
      }
      
      if (updates.length === 0) {
        toast.info("No changes to update.");
        setModalLoading(false);
        return;
      }
      
      const token = localStorage.getItem("token");
      for (const update of updates) {
        await axios.put(`${ipconfig}/admin/subscriptions/update-expiry`, {
          licenseNumber,
          ...update
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      
      toast.success("License updated successfully.");
      setIsModalOpen(false);
      fetchSubscriptionData();
    } catch (error) {
      console.error("Error updating services:", error);
      toast.error("Failed to update license services.");
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    if (data?._id) {
      fetchSubscriptionData();
    }
  }, [data?._id]);

  if (loading) {
    return <Spinner height="100%" />;
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary", display: "flex", alignItems: "center", gap: "10px" }}>
            <PaymentIcon color="primary" />
            Subscription Details
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Manage active services and plans
          </Typography>
        </Box>
      </Box>

      <Divider />

      {licenses.length > 0 ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "24px" }}>
          {licenses.map((license, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{
                p: "20px",
                borderRadius: "12px",
                borderColor: "divider",
                backgroundColor: theme.palette.mode === "light" ? "background.paper" : "#1e293b30",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, pb: "10px", borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ fontWeight: 700, color: "text.primary", fontSize: "15px" }}>
                  License: {license.tallyLicenseNumber}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => checkLicenseStatus(license.tallyLicenseNumber)}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {readOnly ? "View Details" : "Manage"}
                </Button>
              </Box>
              
              <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {["accosync", "analytics", "mart"].map((svc) => {
                  let service = license[`${svc}optedservice`];
                  if (!service && license.services) {
                    if (license.services[svc]) service = license.services[svc];
                    else if (svc === 'accosync') service = license.services;
                  }

                  const isActive = service && service.status === "active";

                  return (
                    <Box
                      key={svc}
                      sx={{
                        p: "12px",
                        borderRadius: "8px",
                        backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b20",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography sx={{ textTransform: "capitalize", fontWeight: 700, fontSize: "13px", color: "text.primary" }}>
                          {svc} Service
                        </Typography>
                        {service ? (
                          <Chip 
                            label={service.status || 'N/A'} 
                            size="small" 
                            color={isActive ? 'success' : 'warning'}
                            icon={isActive ? <DoneIcon /> : <HighlightOffIcon />}
                            sx={{ textTransform: 'capitalize', fontWeight: 700, fontSize: "10px", height: "20px" }}
                          />
                        ) : (
                          <Chip label="Not Opted" size="small" variant="outlined" sx={{ fontSize: "10px", height: "20px" }} />
                        )}
                      </Box>
                      {service && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", mt: 1 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', justifyContent: "space-between" }}>
                            <span>Plan:</span>
                            <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>{service.name || 'N/A'}</span>
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', justifyContent: "space-between" }}>
                            <span>Expiry:</span>
                            <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>{service.expiryDate || 'N/A'}</span>
                          </Typography>
                          {svc === 'accosync' && service.allowedUserAndDevices !== undefined && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', justifyContent: "space-between" }}>
                              <span>Allowed Devices:</span>
                              <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>{service.allowedUserAndDevices}</span>
                            </Typography>
                          )}
                          {svc === 'analytics' && service.NumberOfAllowedCompanies !== undefined && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', justifyContent: "space-between" }}>
                              <span>Allowed Companies:</span>
                              <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>{service.NumberOfAllowedCompanies}</span>
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Box sx={{ p: 6, border: "1px dashed", borderColor: "divider", borderRadius: "12px", textAlign: "center" }}>
          <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 500 }}>
            No subscription data found for this user.
          </Typography>
        </Box>
      )}

      {/* Subscription Dialog / Management Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" }
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, pb: 1 }}>
          License Management
          <IconButton onClick={() => setIsModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ p: "24px" }}>
          {modalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : modalLicenseData ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                  Tally License
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "primary.main" }}>
                  {modalLicenseData.tallyLicenseNumber}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {["accosync", "analytics", "mart"].map((svc) => {
                  let service = modalLicenseData[`${svc}optedservice`];
                  if (!service && modalLicenseData.services) {
                    if (modalLicenseData.services[svc]) service = modalLicenseData.services[svc];
                    else if (svc === 'accosync') service = modalLicenseData.services;
                  }
                  
                  const isOpted = service && (service.expiryDate || service.status);
                  const isSvcActive = (serviceUpdates[svc]?.status || service?.status) === "active";

                  if (!isOpted) {
                    return (
                      <Grid item xs={12} md={4} key={svc}>
                        <Box
                          sx={{
                            p: "24px",
                            borderRadius: "12px",
                            border: "1px dashed",
                            borderColor: "divider",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b20",
                            minHeight: "220px",
                            textAlign: "center"
                          }}
                        >
                          <HighlightOffIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                          <Typography sx={{ textTransform: "capitalize", fontWeight: 700, color: "text.secondary" }}>
                            {svc} Service
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.disabled", mt: 0.5 }}>
                            Not Opted by User
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  }

                  return (
                    <Grid item xs={12} md={4} key={svc}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: "12px",
                          borderColor: "divider",
                          height: "100%"
                        }}
                      >
                        <CardContent sx={{ p: "20px !important", display: "flex", flexDirection: "column", gap: "16px" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography sx={{ textTransform: "capitalize", fontWeight: 800, fontSize: "16px", color: "text.primary" }}>
                              {svc} Service
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch 
                                  size="medium"
                                  checked={isSvcActive}
                                  disabled={readOnly}
                                  onChange={(e) => {
                                    const newStatus = e.target.checked ? 'active' : 'expired';
                                    setServiceUpdates((prev: any) => ({
                                      ...prev,
                                      [svc]: { ...prev[svc], status: newStatus }
                                    }));
                                  }}
                                />
                              }
                              label={
                                <Chip
                                  label={isSvcActive ? "Active" : "Expired"}
                                  size="small"
                                  color={isSvcActive ? "success" : "error"}
                                  sx={{ fontWeight: 700, fontSize: "10px", height: "18px" }}
                                />
                              }
                              labelPlacement="start"
                              sx={{ m: 0, gap: "6px" }}
                            />
                          </Box>

                          <Divider />

                          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>Current Plan:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>{service.name || 'N/A'}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>Current Expiry:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>{service.expiryDate || 'N/A'}</Typography>
                            </Box>
                          </Box>

                          <Divider />

                          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            <TextField
                              label="New Expiry Date"
                              type="date"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              disabled={readOnly}
                              value={serviceUpdates[svc].expiry}
                              onChange={(e) => {
                                setServiceUpdates((prev: any) => ({
                                  ...prev,
                                  [svc]: { ...prev[svc], expiry: e.target.value }
                                }));
                              }}
                            />
                            {svc === 'accosync' && (
                              <TextField
                                label="Allowed Devices"
                                type="number"
                                size="small"
                                fullWidth
                                disabled={readOnly}
                                value={serviceUpdates[svc].allowedUsers}
                                placeholder={service.allowedUserAndDevices?.toString() || "0"}
                                onChange={(e) => {
                                  setServiceUpdates((prev: any) => ({
                                    ...prev,
                                    [svc]: { ...prev[svc], allowedUsers: e.target.value }
                                  }));
                                }}
                              />
                            )}
                            {svc === 'analytics' && (
                              <TextField
                                label="Allowed Companies"
                                type="number"
                                size="small"
                                fullWidth
                                disabled={readOnly}
                                value={serviceUpdates[svc].allowedCompanies}
                                placeholder={service.NumberOfAllowedCompanies?.toString() || "0"}
                                onChange={(e) => {
                                  setServiceUpdates((prev: any) => ({
                                    ...prev,
                                    [svc]: { ...prev[svc], allowedCompanies: e.target.value }
                                  }));
                                }}
                              />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ) : (
            <Typography>No license subscription details found.</Typography>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: "16px 24px" }}>
          <Button onClick={() => setIsModalOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          {!readOnly && (
            <Button
              variant="contained"
              onClick={handleUpdateAll}
              disabled={modalLoading}
              sx={{ textTransform: "none", fontWeight: 600, px: 4 }}
            >
              Update License Details
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      <ToastContainer style={{ width: "auto" }} />
    </Box>
  );
}
